import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../AdminManageCategories/AdminManageCategories.css';
import { createAttribute, deleteAttribute, getAllAttributes, updateAttribute } from '../../firebase/attributes';
import { buildCategoryTree } from '../../firebase/categories';
import { useCategoryManagement } from '../../hooks/useCategoryManagement';

function ProductTabBar({ active }) {
    const navigate = useNavigate();
    const tabs = [
        { label: 'Manage Products', path: '/admin/products/manage' },
        { label: 'Manage Category', path: '/admin/products/categories' },
        { label: 'Manage Subcategory', path: '/admin/products/subcategories' },
        { label: 'Manage Submenus', path: '/admin/products/submenus' },
        { label: 'Manage Attributes', path: '/admin/products/attributes' },
        { label: 'Manage Inventory', path: '/admin/products/inventory' },
    ];
    return <div className="mc-tab-bar">{tabs.map((tab) => <button key={tab.path} className={`mc-tab${active === tab.label ? ' mc-tab--active' : ''}`} onClick={() => navigate(tab.path)}>{tab.label}</button>)}</div>;
}

function AttributeModal({ attribute, mainCategories, subcategories, onClose, onSave, error, saving }) {
    const [form, setForm] = useState({
        name: attribute?.name || '',
        status: attribute?.status || 'active',
        type: attribute?.type || 'select',
        values: attribute?.values || [],
        subcategoryIds: attribute?.subcategoryIds || [],
        categoryIds: attribute?.categoryIds || [],
    });
    const [valueInput, setValueInput] = useState('');
    const [colorInput, setColorInput] = useState('#ff0000');
    const [localError, setLocalError] = useState('');

    const addValue = () => {
        const value = valueInput.trim();
        if (!value || form.values.some((item) => item.name.toLowerCase() === value.toLowerCase())) return;
        setForm((prev) => ({ ...prev, values: [...prev.values, { name: value, status: 'active' }] }));
        setValueInput('');
    };
    const toggleSubcategory = (id) => setForm((prev) => ({
        ...prev,
        subcategoryIds: prev.subcategoryIds.includes(id) ? prev.subcategoryIds.filter((item) => item !== id) : [...prev.subcategoryIds, id],
    }));
    const toggleCategory = (id) => setForm((prev) => ({
        ...prev,
        categoryIds: prev.categoryIds.includes(id) ? prev.categoryIds.filter((item) => item !== id) : [...prev.categoryIds, id],
    }));
    const submit = () => {
        setLocalError('');
        if (!form.name.trim()) {
            setLocalError('Attribute name is required.');
            return;
        }
        const selectedSubNames = subcategories.filter((sub) => form.subcategoryIds.includes(sub.id)).map((sub) => sub.name);
        const selectedCatNames = mainCategories.filter((cat) => form.categoryIds.includes(cat.id)).map((cat) => cat.name);
        const cleanedValues = form.values.map((v) => ({
            name: v.name,
            status: v.status,
        }));
        onSave({ ...form, values: cleanedValues, subcategoryNames: selectedSubNames, categoryNames: selectedCatNames });
    };

    const displayError = localError || error;

    const updateValue = (index, field, value) => {
        setForm((prev) => {
            const next = [...prev.values];
            next[index] = { ...next[index], [field]: value };
            return { ...prev, values: next };
        });
    };

    return (
        <div className="mc-modal-overlay" onClick={onClose}>
            <div className="mc-modal" onClick={(event) => event.stopPropagation()}>
                <div className="mc-modal-header"><h2 className="mc-modal-title">{attribute ? 'Edit Attribute' : 'Add Attribute'}</h2><button className="mc-modal-close" onClick={onClose}>×</button></div>
                <div className="mc-modal-body">
                    {displayError && <div className="mc-info-banner" style={{ width: '100%' }}>{displayError}</div>}
                    <div className="mc-field-row"><div className="mc-field"><label>Attribute Name</label><input value={form.name} onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))} placeholder="e.g. Size" /></div><div className="mc-field"><label>Status</label><select value={form.status} onChange={(event) => setForm((prev) => ({ ...prev, status: event.target.value }))}><option value="active">Active</option><option value="inactive">Inactive</option></select></div></div>
                    <div className="mc-field"><label>Input Type</label><select value={form.type} onChange={(event) => setForm((prev) => ({ ...prev, type: event.target.value }))}><option value="select">Dropdown (Select)</option><option value="radio">Radio Buttons</option><option value="checkbox">Checkboxes</option><option value="text">Text Input</option><option value="color">Color Toggle</option></select><p className="mc-field-hint">Determines how this attribute is displayed in the product form.</p></div>
                    {!(form.type === 'text' && form.values.length > 0) && form.type !== 'color' && (
                        <div className="mc-field"><label>Attribute Values</label><div className="mc-sub-input-row">
                                <input value={valueInput} onChange={(event) => setValueInput(event.target.value)} onKeyDown={(event) => event.key === 'Enter' && addValue()} placeholder={form.type === 'text' ? "e.g. Enter value..." : "e.g. Red"} />
                            <button type="button" className="mc-sub-add-btn" onClick={addValue}>Add</button>
                        </div></div>
                    )}
                    <div className="mc-structured-list">{form.values.map((value, index) => <div key={`${value.name}-${index}`} className="mc-tree-item"><span style={{ minWidth: 80, fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {form.type === 'color' && <span style={{ width: '20px', height: '20px', backgroundColor: value.name, border: '1px solid #ccc', borderRadius: '4px' }}></span>}
                        {value.name}
                    </span>
                        <select value={value.status} onChange={(e) => updateValue(index, 'status', e.target.value)} style={{ padding: '3px 6px', border: '1px solid #d0d0d0', borderRadius: 6, fontSize: 12 }}><option value="active">Active</option><option value="inactive">Inactive</option></select>
                        <button type="button" className="mc-mini-danger" onClick={() => setForm((prev) => ({ ...prev, values: prev.values.filter((_, i) => i !== index) }))}>Delete</button>
                    </div>)}</div>
                    {mainCategories.length > 0 && (
                        <div className="mc-field"><label>Assign to Categories</label><div className="mc-check-list">{mainCategories.map((cat) => <label key={cat.id} className="mc-check-row"><input type="checkbox" checked={form.categoryIds.includes(cat.id)} onChange={() => toggleCategory(cat.id)} />{cat.name}</label>)}</div></div>
                    )}
                    {subcategories.length > 0 && form.categoryIds.length > 0 && (
                        <div className="mc-field"><label>Assign to Subcategories</label><div className="mc-check-list">{subcategories.filter((sub) => form.categoryIds.includes(sub.parentId)).map((subcategory) => <label key={subcategory.id} className="mc-check-row"><input type="checkbox" checked={form.subcategoryIds.includes(subcategory.id)} onChange={() => toggleSubcategory(subcategory.id)} />{subcategory.parentName} / {subcategory.name}</label>)}</div></div>
                    )}
                </div>
                <div className="mc-modal-footer"><button className="mc-btn-secondary" onClick={onClose} disabled={saving}>Cancel</button><button className="mc-btn-primary" onClick={submit} disabled={saving}>{saving ? 'Saving...' : 'Save Attribute'}</button></div>
            </div>
        </div>
    );
}

function formatValueSummary(values = []) {
    return String(values.length);
}

function getCategoryNames(attribute, mainCategories) {
    const names = attribute.categoryNames || [];
    const ids = attribute.categoryIds || [];
    if (names.length > 0) return names.join(', ');
    if (ids.length > 0) return mainCategories.filter((c) => ids.includes(c.id)).map((c) => c.name).join(', ');
    return '';
}

export default function AdminManageAttributes() {
    const { categories, fetchCategories } = useCategoryManagement([]);
    const [attributes, setAttributes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [modal, setModal] = useState(null);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [filterType, setFilterType] = useState('all');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const load = async () => {
        setLoading(true);
        const result = await getAllAttributes();
        if (result.error) setError(result.error);
        else setAttributes(result.attributes);
        setLoading(false);
    };
    useEffect(() => { fetchCategories(); load(); }, [fetchCategories]);
    const tree = useMemo(() => buildCategoryTree(categories), [categories]);
    const mainCategories = useMemo(() => tree.map(({ subcategories, ...cat }) => cat), [tree]);
    const subcategories = useMemo(() => tree.flatMap((main) => main.subcategories.filter((sub) => !sub.isLegacy).map((sub) => ({ ...sub, parentName: main.name, parentId: main.id }))), [tree]);

    const filteredAttributes = useMemo(() => {
        const q = search.trim().toLowerCase();
        let result = attributes;
        if (statusFilter !== 'all') {
            result = result.filter((attr) => attr.status === statusFilter);
        }
        if (filterType !== 'all') {
            result = result.filter((attr) => (attr.type || 'select') === filterType);
        }
        if (!q) return result;
        return result.filter((attr) =>
            (attr.name || '').toLowerCase().includes(q) ||
            ((attr.subcategoryNames || []).join(', ')).toLowerCase().includes(q) ||
            ((attr.categoryNames || []).join(', ')).toLowerCase().includes(q)
        );
    }, [attributes, search, statusFilter, filterType]);

    useEffect(() => { setPage(1); }, [search, statusFilter, filterType]);
    const totalPages = Math.max(1, Math.ceil(filteredAttributes.length / pageSize));
    const paginatedRows = filteredAttributes.slice((page - 1) * pageSize, page * pageSize);

    const hasActiveFilters = statusFilter !== 'all' || search.trim() !== '' || filterType !== 'all';

    const [saving, setSaving] = useState(false);

    const save = async (payload) => {
        setSaving(true);
        setError(null);
        const result = modal?.id ? await updateAttribute(modal.id, payload) : await createAttribute(payload);
        setSaving(false);
        if (result.error) {
            setError(result.error);
            return;
        }
        setModal(null);
        setError(null);
        load();
    };
    const remove = async (attribute) => {
        const result = await deleteAttribute(attribute.id);
        if (result.error) setError(result.error);
        else {
            setError(null);
            load();
        }
    };
    
    return (
        <div className="mc-page">
            <ProductTabBar active="Manage Attributes" />
            {loading && <div className="mc-info-banner">Loading attributes...</div>}
            {error && <div className="mc-info-banner">{error}</div>}

            <div className="mc-toolbar">
                <div className="mc-toolbar-left">
                    <div className="mc-entries-label">
                        Show
                        <select className="mc-entries-select" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                            <option value="all">All Statuses</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </select>
                    </div>
                    {hasActiveFilters && (
                        <button className="mc-filter-clear" onClick={() => { setStatusFilter('all'); setSearch(''); setFilterType('all'); setPage(1); }}>Clear filters</button>
                    )}
                </div>
                <div className="mc-toolbar-right">
                    <div className="mc-search-wrap">
                        <input className="mc-search" value={search} onChange={(event) => { setSearch(event.target.value); setPage(1); }} placeholder="Search attributes..." />
                    </div>
                    <button className="mc-btn-add" onClick={() => setModal({})}>Add Attribute</button>
                </div>
            </div>

            {/* Filter bar */}
            <div className="mi-filter-bar">
                <div className="mi-filter-group">
                    <label className="mi-filter-label">Input Type</label>
                    <select className="mi-filter-select" value={filterType} onChange={(e) => { setFilterType(e.target.value); setPage(1); }}>
                        <option value="all">All Types</option>
                        <option value="select">Dropdown</option>
                        <option value="radio">Radio</option>
                        <option value="checkbox">Checkbox</option>
                        <option value="text">Text</option>
                        <option value="color">Color</option>
                    </select>
                </div>
                <div className="mi-filter-group">
                    <label className="mi-filter-label">Page Size</label>
                    <select className="mi-filter-select" value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
                        {[10, 15, 20, 25].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                </div>
            </div>

            <div className="mc-table-wrap">
                <table className="mc-table">
                    <thead><tr><th>Attribute</th><th>Type</th><th>Values</th><th>Assigned Categories</th><th>Assigned Subcategories</th><th>Status</th><th>Actions</th></tr></thead>
                    <tbody>
                        {paginatedRows.length === 0 ? <tr><td colSpan={7} className="mc-empty">No attributes found.</td></tr> : paginatedRows.map((attribute) => (
                            <tr key={attribute.id}>
                                <td className="mc-cat-name">{attribute.name}</td>
                                <td><span className="mc-status-badge">{(attribute.type || 'select').charAt(0).toUpperCase() + (attribute.type || 'select').slice(1)}</span></td>
                                <td>{formatValueSummary(attribute.values)}</td>
                                <td>{(attribute.categoryNames || []).join(', ') || (attribute.categoryIds || []).length > 0 ? getCategoryNames(attribute, mainCategories) : '—'}</td>
                                <td>{(attribute.subcategoryNames || []).join(', ') || '—'}</td>
                                <td><span className={`mc-status-badge mc-status-badge--${attribute.status || 'active'}`}>{attribute.status === 'inactive' ? 'Inactive' : 'Active'}</span></td>
                                <td>
                                    <div className="mc-actions">
                                        <button className="mc-action-btn mc-action-btn--edit" onClick={() => setModal(attribute)} title="Edit this attribute">
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                            <span className="mc-action-label">Edit</span>
                                        </button>
                                        <button className="mc-action-btn mc-action-btn--delete" onClick={() => remove(attribute)} title="Delete this attribute">
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                                            <span className="mc-action-label">Delete</span>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {filteredAttributes.length > pageSize && (
                <div className="mc-pagination">
                    <span className="mc-result-count">
                        Showing {Math.min((page - 1) * pageSize + 1, filteredAttributes.length)}–{Math.min(page * pageSize, filteredAttributes.length)} of {filteredAttributes.length}
                    </span>
                    <button className="mc-page-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Previous</button>
                    <span className="mc-page-divider">|</span>
                    <button className="mc-page-btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
                </div>
            )}

            {modal && <AttributeModal attribute={modal.id ? modal : null} mainCategories={mainCategories} subcategories={subcategories} onClose={() => { setModal(null); setError(null); }} onSave={save} error={error} saving={saving} />}
        </div>
    );
}